// Service to fetch resume data from Google Drive
export class ResumeService {
  // Fetch resume from Google Drive with CORS handling
  static async getResumeFromDrive(driveUrl) {
    try {
      // Convert Google Drive sharing URL to direct download URL
      const fileId = this.extractFileId(driveUrl);
      if (!fileId) {
        throw new Error('Invalid Google Drive URL');
      }

      // Since Google Drive has CORS restrictions, we'll use a different approach
      // Option 1: Try to use Google Drive API (requires API key)
      // Option 2: Use a CORS proxy service
      // Option 3: Fall back to static data (current approach)
      
      const urls = [
        // Try direct download (likely to fail due to CORS)
        `https://drive.google.com/uc?export=download&id=${fileId}`,
        // Try alternative format
        `https://drive.google.com/file/d/${fileId}/view?usp=sharing`,
        // Try with different parameters
        `https://drive.google.com/uc?export=download&confirm=t&id=${fileId}`
      ];

      let response = null;
      let lastError = null;

      // Try each URL until one works
      for (const url of urls) {
        try {
          response = await fetch(url, {
            method: 'GET',
            headers: {
              'Accept': 'text/plain,application/json,*/*',
              'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
            },
            mode: 'cors'
          });
          
          if (response.ok) {
            console.log(`Successfully fetched from ${url}`);
            break; // Success, exit the loop
          }
        } catch (error) {
          lastError = error;
          console.warn(`Failed to fetch from ${url}:`, error.message);
          continue; // Try next URL
        }
      }

      // If all URLs failed due to CORS, log the issue and fall back
      if (!response || !response.ok) {
        console.warn('Google Drive CORS issue detected. This is expected behavior.');
        console.warn('To fix this, you can:');
        console.warn('1. Set up a CORS proxy server');
        console.warn('2. Use Google Drive API with proper authentication');
        console.warn('3. Host the resume file on a CORS-enabled service');
        console.warn('4. Use the static fallback data (current behavior)');
        
        // Return static data instead of throwing error
        return this.getStaticResumeData();
      }

      const text = await response.text();
      return this.parseResumeText(text);
    } catch (error) {
      console.error('Error fetching resume from Google Drive:', error);
      console.log('Falling back to static resume data');
      return this.getStaticResumeData();
    }
  }

  // Extract file ID from Google Drive URL
  static extractFileId(url) {
    const patterns = [
      /\/file\/d\/([a-zA-Z0-9-_]+)/,
      /id=([a-zA-Z0-9-_]+)/,
      /\/d\/([a-zA-Z0-9-_]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }
    return null;
  }

  // Parse resume text and extract structured data
  static parseResumeText(text) {
    try {
      // Try to parse as JSON first (if it's a structured format)
      const jsonData = JSON.parse(text);
      return this.formatStructuredResume(jsonData);
    } catch {
      // If not JSON, parse as plain text
      return this.parsePlainTextResume(text);
    }
  }

  // Parse structured JSON resume
  static formatStructuredResume(data) {
    let formattedData = '';

    if (data.basics) {
      formattedData += `### ${data.basics.name || 'Dhrumit Patel'}\n\n`;
      if (data.basics.summary) {
        formattedData += `**Summary:** ${data.basics.summary}\n\n`;
      }
      if (data.basics.email) {
        formattedData += `**Email:** ${data.basics.email}\n`;
      }
      if (data.basics.phone) {
        formattedData += `**Phone:** ${data.basics.phone}\n`;
      }
      if (data.basics.location) {
        formattedData += `**Location:** ${data.basics.location.city}, ${data.basics.location.region}\n`;
      }
      formattedData += `\n`;
    }

    if (data.skills) {
      formattedData += `### Skills & Technologies\n\n`;
      data.skills.forEach(skill => {
        formattedData += `**${skill.name}:** ${skill.keywords.join(', ')}\n`;
      });
      formattedData += `\n`;
    }

    if (data.work) {
      formattedData += `### Work Experience\n\n`;
      data.work.forEach(job => {
        formattedData += `**${job.position}** at ${job.company}\n`;
        formattedData += `*${job.startDate} - ${job.endDate || 'Present'}*\n`;
        if (job.summary) {
          formattedData += `${job.summary}\n`;
        }
        if (job.highlights) {
          job.highlights.forEach(highlight => {
            formattedData += `• ${highlight}\n`;
          });
        }
        formattedData += `\n`;
      });
    }

    if (data.education) {
      formattedData += `### Education\n\n`;
      data.education.forEach(edu => {
        formattedData += `**${edu.studyType} in ${edu.area}**\n`;
        formattedData += `${edu.institution} - ${edu.endDate}\n`;
        if (edu.gpa) {
          formattedData += `GPA: ${edu.gpa}\n`;
        }
        formattedData += `\n`;
      });
    }

    if (data.projects) {
      formattedData += `### Projects\n\n`;
      data.projects.forEach(project => {
        formattedData += `**${project.name}**\n`;
        if (project.description) {
          formattedData += `${project.description}\n`;
        }
        if (project.technologies) {
          formattedData += `**Technologies:** ${project.technologies.join(', ')}\n`;
        }
        if (project.url) {
          formattedData += `**Link:** ${project.url}\n`;
        }
        formattedData += `\n`;
      });
    }

    return formattedData;
  }

  // Parse plain text resume
  static parsePlainTextResume(text) {
    // This is a fallback for plain text resumes
    // You can customize this based on your resume format
    return text;
  }

  // Get static fallback data if Google Drive is unavailable
  static getStaticResumeData() {
    return `
### Dhrumit Patel – AI Engineer & Full Stack Developer

**Contact Information:**
- 📍 Toronto, ON, Canada
- 📧 Email: dhrumitpatel.ai@gmail.com
- 🔗 LinkedIn: [linkedin.com/in/dhrumitpatelds](https://linkedin.com/in/dhrumitpatelds)
- 💻 Portfolio: [dhrumitpatel.dev](https://dhrumitpatel.com)
- 🐙 GitHub: [github.com/Dhrumit1314](https://github.com/Dhrumit1314)
- 🤗 Hugging Face: [huggingface.co/Dhrumit1314](https://huggingface.co/Dhrumit1314)

**🎯 Professional Summary:**
Experienced and results-driven AI Engineer and Full Stack Developer with specialized expertise in Machine Learning, NLP, Computer Vision, and Financial Crime Model Validation. Currently working at Royal Bank of Canada as Manager of Global FC/AML Model Validation, where I apply advanced machine learning techniques to validate financial crime detection models. Skilled in Python, SQL, Java, and JavaScript with extensive experience in full-stack development, cloud computing, and DevOps. Proven ability in building AI-driven applications, collaborating in agile teams, and ensuring regulatory compliance. Proficient with Docker, AWS, Azure, and modern data tools.

**🧠 Skills:**
- **Languages:** Python, SQL, Java, JavaScript
- **Machine Learning & NLP:** Scikit-learn, Deep Learning, NLP Techniques, LLMs
- **Information Retrieval & Recommenders:** Search algorithms, classification, evaluation metrics
- **Frontend:** React.js, HTML, CSS
- **Backend:** Node.js, Express.js
- **Databases:** MySQL, PostgreSQL, SQL Server
- **Cloud & DevOps:** AWS, Microsoft Azure, Docker, CI/CD Pipelines
- **Data Analysis & Visualization:** Pandas, NumPy, Matplotlib, Seaborn, Plotly, Power BI
- **Tools:** Git, Docker, WebSockets
- **Soft Skills:** Strong communication, collaborative teamwork, English proficiency

**🎓 Education:**
**Advanced Diploma in Software Engineering Technology – Artificial Intelligence**  
*Centennial College, Toronto, ON*  
*April 2024*  
**Relevant Coursework:** Full Stack Development, Machine/Deep Learning, Data Analysis, Cloud Computing

**💼 Experience:**
**Manager, Global FC/AML Model Validation**  
*Royal Bank of Canada, Toronto, ON*  
*Oct 2024 – Present*
- Applied machine learning and statistical techniques to support the validation of Financial Crime models, including transaction monitoring, sanctions screening, and customer risk scoring models.
- Assessed model performance and conceptual soundness through quantitative testing, data integrity checks, and implementation reviews, aligning with regulatory standards (OSFI E-23, SR 11-7).
- Developed and executed validation frameworks using Python and SQL, incorporating sensitivity testing, benchmarking, and scenario analysis.
- Collaborated with cross-functional teams including Compliance, Data Science, and Technology to review model assumptions, logic, thresholds, and production implementation.
- Authored clear and detailed validation reports summarizing findings, limitations, and remediation actions, and communicated results to Risk Committees and senior stakeholders.
- Ensured governance compliance through robust documentation and supported regulatory inquiries from FINTRAC and OSFI.

**Full Stack Developer (Intern)**  
*Centennial College – ARIES, Toronto, ON*  
*Sept 2023 – Dec 2023*
- Developed backend services for vehicle management with secure CRUD operations.
- Strengthened system security using multi-layer authentication techniques.
- Integrated WebSockets for real-time communication, boosting responsiveness by 30%.
- Utilized Docker to ensure scalable and environment-consistent deployments.
- Contributed to full-stack features using Express.js, supporting high-performance web apps.

**🛠️ Key Projects:**
**1. Face Recognition Sign-In/Sign-Out Application**  
- **Tech Stack:** Python, AWS (Lambda, S3, DynamoDB, Rekognition), OpenCV, Tkinter  
- **Description:** Built a secure and automated attendance system using face recognition technology. Designed GUI in Tkinter and used AWS cloud services for backend processes.  
- **Features:** Real-time face detection, secure authentication, automated attendance tracking
- **GitHub:** [View Project](https://github.com/Dhrumit1314/face-recognition-attendance)

**2. Facial Emotion, Age & Gender Detection**  
- **Tech Stack:** Python, OpenCV, Deep Learning, TensorFlow  
- **Description:** Developed an advanced facial analysis tool achieving:
  - 95% accuracy in emotion detection  
  - 90% in age prediction  
  - 98% in gender recognition  
- **Features:** Real-time analysis, multiple emotion categories, age range prediction
- **GitHub:** [View Project](https://github.com/Dhrumit1314/facial-analysis)

**3. AI-Powered Portfolio Assistant**  
- **Tech Stack:** React.js, Google Gemini API, Node.js, SCSS  
- **Description:** Intelligent chatbot for portfolio websites with real-time data integration
- **Features:** Resume-based responses, GitHub/Hugging Face integration, real-time profile data
- **Live Demo:** [Current Portfolio](https://dhrumitpatel.dev)

**4. Financial Crime Model Validation Framework**  
- **Tech Stack:** Python, SQL, Statistical Analysis, Regulatory Compliance  
- **Description:** Developed comprehensive validation frameworks for FC/AML models at RBC
- **Features:** Model performance assessment, regulatory compliance, risk scoring validation

**🏆 Achievements & Certifications:**
- Successfully validated multiple FC/AML models at RBC, ensuring regulatory compliance
- Achieved 95%+ accuracy in facial recognition and emotion detection projects
- Contributed to open-source projects on GitHub and Hugging Face
- Completed Advanced Diploma in Software Engineering Technology – AI from Centennial College

**📌 Additional Information:**
- **Languages:** English (Fluent), Gujarati (Native)
- **Interests:** Machine Learning, Computer Vision, Financial Technology, Open Source
- **Availability:** Open to interesting opportunities and collaborations
- **More Projects:** Visit my [GitHub](https://github.com/Dhrumit1314) and [Hugging Face](https://huggingface.co/Dhrumit1314) profiles for additional projects and contributions
`;
  }
} 